export type TenantId = 'honeyshares' | 'redex' | 'emerge' | (string & {});

export type TenantFeatures = {
  plaid: boolean;
  ach: boolean;
  crypto: boolean;
  invoices: boolean;
  shareConversion: boolean;
  /**
   * Whether this tenant sells investments at all.
   *
   * Gates every investor-facing surface — KYC, accreditation, bank and wallet
   * sections in Settings, the "Add Investor" flow in Users, public self-signup
   * with an accreditation wizard. eMerge is a hearing-care company; none of it
   * applies, and showing it anyway confused the first employees to log in.
   *
   * A flag rather than `TENANT.id !== 'emerge'` because a fourth tenant should
   * declare what it is, not be inferred from what it is not.
   */
  investing: boolean;
};

export type InvestCategory = {
  value: string;
  label: string;
};

export type TenantBranding = {
  name: string;
  supportEmail: string;
  propertyLabel: string;
  logoPrimaryPath: string;
  /**
   * The image behind a public event funnel page.
   *
   * Declared per tenant rather than chosen at the point of use, because it was
   * a two-way ternary — `redex ? redex-og-image : HiveFund.jpg` — in a
   * registry with four tenants. Everything that was not REDEX inherited a
   * Honeyshares real-estate photograph, so eMerge's public conference page led
   * with a picture of houses. The same shape as the signing page that branded
   * every non-REDEX tenant "Honeyshares".
   */
  eventHeroPath: string;
  logoLightPath: string;
};

export type TenantDefaults = {
  apiBaseUrl: string;
  wsUrl: string;
};

export type TenantConfig = {
  id: TenantId;
  branding: TenantBranding;
  features: TenantFeatures;
  defaults: TenantDefaults;
  investCategories: InvestCategory[];
};

const IS_REDEX_BUILD = process.env.REACT_APP_TENANT_ID === 'redex';

const REGISTRY: Record<string, Omit<TenantConfig, 'id'>> = IS_REDEX_BUILD
  ? {
      redex: {
        branding: {
          name: 'REDEX',
          supportEmail: 'support@redexct.xyz',
          propertyLabel: 'Assets',
          logoPrimaryPath: '/redex-logo.jpg',
          eventHeroPath: '/redex-og-image.png',
          logoLightPath: '/redex-logo.jpg',
        },
        features: {
          plaid: true,
          ach: true,
          crypto: true,
          invoices: true,
          shareConversion: true,
          investing: true,
        },
        defaults: {
          apiBaseUrl: 'https://portal.redexct.com/api/v1',
          wsUrl: 'wss://portal.redexct.com',
        },
        investCategories: [
          { value: 'all', label: 'All' },
          { value: 'license', label: 'Licenses' },
          { value: 'mining', label: 'Mining' },
          { value: 'fund', label: 'Funds' },
          { value: 'debt', label: 'Debt' },
          { value: 'development', label: 'Infrastructure' },
        ],
      },
    }
  : {
      honeyshares: {
        branding: {
          name: 'Honeyshares',
          supportEmail: 'support@honeyshares.com',
          propertyLabel: 'Properties',
          logoPrimaryPath: '/Logos/logo-all-black.png',
          eventHeroPath: '/HiveFund.jpg',
          logoLightPath: '/Logos/logo-all-white.png',
        },
        features: {
          plaid: true,
          ach: true,
          crypto: false,
          invoices: false,
          shareConversion: false,
          investing: true,
        },
        defaults: {
          apiBaseUrl: 'https://portal.honeyshares.com/api/v1',
          wsUrl: 'wss://portal.honeyshares.com',
        },
        investCategories: [
          { value: 'all', label: 'All' },
          { value: 'single-family', label: 'Single Family' },
          { value: 'vacation-rental', label: 'Vacation Rental' },
          { value: 'multi-family', label: 'Multi-Family' },
          { value: 'fund', label: 'Funds' },
        ],
      },
      redex: {
        branding: {
          name: 'REDEX',
          supportEmail: 'support@redexct.xyz',
          propertyLabel: 'Assets',
          logoPrimaryPath: '/redex-logo.jpg',
          eventHeroPath: '/redex-og-image.png',
          logoLightPath: '/redex-logo.jpg',
        },
        features: {
          plaid: true,
          ach: true,
          crypto: true,
          invoices: true,
          shareConversion: true,
          investing: true,
        },
        defaults: {
          apiBaseUrl: 'https://portal.redexct.com/api/v1',
          wsUrl: 'wss://portal.redexct.com',
        },
        investCategories: [
          { value: 'all', label: 'All' },
          { value: 'license', label: 'Licenses' },
          { value: 'mining', label: 'Mining' },
          { value: 'fund', label: 'Funds' },
          { value: 'debt', label: 'Debt' },
          { value: 'development', label: 'Infrastructure' },
        ],
      },
      emerge: {
        branding: {
          name: 'eMerge',
          supportEmail: 'support@emergesc.com',
          propertyLabel: 'Facilities',
          logoPrimaryPath: '/emerge-logo.png',
          // The landing page's own opening photograph. A conference page with
          // no image configured should look like eMerge, not like another
          // company's asset library.
          eventHeroPath: '/emerge/2173110193.jpg',
          logoLightPath: '/emerge-logo.png',
        },
        features: {
          // Chase/J.P. Morgan payment rails aren't wired yet — off until
          // that integration exists. Invoices is on: finance_accounts/
          // invoices are real tables already.
          plaid: false,
          ach: false,
          crypto: false,
          invoices: true,
          shareConversion: false,
          investing: false,
        },
        defaults: {
          apiBaseUrl: 'http://localhost:3011/api/v1',
          wsUrl: 'ws://localhost:3012',
        },
        // Reused as the facility-type filter (SNF/ALF) rather than a real-estate category.
        investCategories: [
          { value: 'all', label: 'All' },
          { value: 'snf', label: 'Skilled Nursing (SNF)' },
          { value: 'alf', label: 'Assisted Living (ALF)' },
        ],
      },
    };

let cached: TenantConfig | null = null;

function envBool(value: string | undefined): boolean | undefined {
  if (value == null) return undefined;
  return value.toLowerCase() === 'true';
}

export function getTenantId(): TenantId {
  return ((process.env.REACT_APP_TENANT_ID || 'honeyshares').trim().toLowerCase() as TenantId);
}

export function getTenantConfig(): TenantConfig {
  const id = getTenantId();
  if (cached?.id === id) return cached;

  const base = REGISTRY[id] || REGISTRY.redex || REGISTRY.honeyshares;

  const config: TenantConfig = {
    id,
    branding: {
      ...base.branding,
      name: process.env.REACT_APP_BRAND_NAME || base.branding.name,
      supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || base.branding.supportEmail,
      propertyLabel: process.env.REACT_APP_PROPERTY_LABEL || base.branding.propertyLabel,
      logoPrimaryPath: process.env.REACT_APP_LOGO_PRIMARY || base.branding.logoPrimaryPath,
      eventHeroPath: process.env.REACT_APP_EVENT_HERO || base.branding.eventHeroPath,
      logoLightPath: process.env.REACT_APP_LOGO_LIGHT || base.branding.logoLightPath,
    },
    features: {
      ...base.features,
      plaid: envBool(process.env.REACT_APP_FEATURE_PLAID) ?? base.features.plaid,
      ach: envBool(process.env.REACT_APP_FEATURE_ACH) ?? base.features.ach,
      crypto: envBool(process.env.REACT_APP_FEATURE_CRYPTO) ?? base.features.crypto,
      invoices: envBool(process.env.REACT_APP_FEATURE_INVOICES) ?? base.features.invoices,
      shareConversion: envBool(process.env.REACT_APP_FEATURE_SHARE_CONVERSION) ?? base.features.shareConversion,
      investing: envBool(process.env.REACT_APP_FEATURE_INVESTING) ?? base.features.investing,
    },
    defaults: {
      ...base.defaults,
      apiBaseUrl: process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : base.defaults.apiBaseUrl),
      wsUrl: process.env.REACT_APP_WS_URL || (typeof window !== 'undefined' ? `wss://${window.location.host}` : base.defaults.wsUrl),
    },
    investCategories: base.investCategories || [
      { value: 'all', label: 'All' },
      { value: 'fund', label: 'Funds' },
    ],
  };

  cached = config;
  return config;
}
